import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, updateDoc, where, increment } from "firebase/firestore";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  LikeBar,
  ReactionBar,
  Bookmark,
  PresenceBadge,
  Discussion,
} from "../components";

function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

function Section({ id, title, children }) {
  const headingId = title ? `${id}-h` : undefined;
  return (
    <section id={id} className="vw-sec" {...(title ? { "aria-labelledby": headingId } : { "aria-label": id })}>
      {title && <h2 className="vw-h2" id={headingId}>{title}</h2>}
      {children}
    </section>
  );
}

function normalizeTip(d = {}) {
  const gallery = Array.isArray(d.gallery) ? d.gallery : [];
  const cover =
    d.coverImage ||
    (gallery.length ? gallery[0] : null) ||
    d.image ||
    d.imageUrl ||
    null;

  const createdAtMs =
    d.createdAt?.toMillis?.() ??
    (typeof d.createdAt === "number" ? d.createdAt : 0);

  return {
    title: d.title || "Untitled Tip",
    summary: d.summary || "",
    content: d.content || "",
    cover,
    gallery,
    authorName: d.authorName || "Anonymous",
    tags: Array.isArray(d.tags) ? d.tags : [],
    createdAtMs,
    createdAtText: createdAtMs ? new Date(createdAtMs).toLocaleDateString() : "",
    videoUrl: d.videoUrl || null,
    reactions: d.reactions || {},
  };
}

export default function TipTrickDetail() {
  const { slugOrId } = useParams(); // define route as :slugOrId
  const location = useLocation();

  const [tip, setTip] = useState(undefined); // undefined=loading, null=404
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const bookmarkKey = useMemo(() => `tips:${slugOrId}`, [slugOrId]);
  const basePath = useMemo(() => ["tips_tricks", slugOrId], [slugOrId]);

  // Load by slug fallback to ID
  useEffect(() => {
    let unsub;
    let cancelled = false;

    async function attachBySlugOrId() {
      // Try slug query first
      const q1 = query(collection(db, "tips_tricks"), where("slug", "==", slugOrId), limit(1));
      const s1 = await getDocs(q1);
      let docId = null;
      if (!s1.empty) docId = s1.docs[0].id;
      else docId = slugOrId;

      unsub = onSnapshot(doc(db, "tips_tricks", docId), (snap) => {
        if (!snap.exists()) {
          if (!cancelled) setTip(null);
          return;
        }
        const raw = snap.data();
        const v = normalizeTip(raw);
        if (!cancelled) {
          setTip({ id: snap.id, ...v, _raw: raw });
        }
      });
    }

    attachBySlugOrId();
    return () => { cancelled = true; unsub && unsub(); };
  }, [slugOrId]);

  // Count views
  useEffect(() => {
    if (!tip?.id) return;
    updateDoc(doc(db, "tips_tricks", tip.id), { views: increment(1) }).catch(() => {});
  }, [tip?.id]);

  const yt = useMemo(() => (tip?.videoUrl ? toYouTubeEmbed(tip.videoUrl) : null), [tip?.videoUrl]);

  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) { await navigator.share({ title: tip?.title ?? "Tip", url }); return; }
      await navigator.clipboard.writeText(url);
    } catch {}
  }, [tip?.title, location.pathname, location.hash]);

  const openLightbox = useCallback((index) => setLightbox({ open: true, index }), []);
  const closeLightbox = useCallback(() => setLightbox({ open: false, index: 0 }), []);
  const nextLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = tip?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index + 1) % len : 0 };
    });
  }, [tip?.gallery?.length]);
  const prevLight = useCallback((e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = tip?.gallery?.length ?? 0;
      return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
    });
  }, [tip?.gallery?.length]);

  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, closeLightbox, nextLight, prevLight]);

  // ----- Loading / 404 -----
  if (tip === undefined) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-skel-card" aria-busy="true">
            <div className="vw-skel-hero" />
            <div className="vw-skel-title" />
            <div className="vw-skel-row" />
            <div className="vw-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }
  if (tip === null) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-empty-card" role="alert">
            <div className="vw-empty-title">We can’t find that tip</div>
            <p className="vw-empty-sub">It may have been removed or the link is incorrect.</p>
            <Link to="/community/tips_tricks" className="vw-btn vw-btn--primary">← Back to Tips & Tricks</Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ----- Render -----
  return (
    <PageWrapper>
      <div className="vw-wrap">
        {/* Top bar */}
        <div className="vw-toprow">
          <Link to="/community/tips_tricks" className="vw-back" aria-label="Back">← All tips</Link>
          <div className="vw-actions">
            {tip.videoUrl && !yt && (
              <a className="vw-action" href={tip.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
            )}
            <button className="vw-action" onClick={onShare}>⤴ Share</button>
          </div>
        </div>

        {/* Hero */}
        <header className="vw-hero-row">
          <div className="vw-hero-left">
            <div className="vw-portrait-wrap">
              {tip.cover ? (
                <img className="vw-portrait" src={tip.cover} alt={`${tip.title} cover`} loading="lazy" />
              ) : (
                <div className="vw-portrait vw-portrait--placeholder" aria-label="No image">💡</div>
              )}
            </div>
          </div>

          <div className="vw-hero-right">
            <div className="vw-chips">
              {tip.tags?.map((t) => <span className="vw-chip" key={t}>#{t}</span>)}
              {tip.createdAtText && <span className="vw-chip">🗓 {tip.createdAtText}</span>}
              <span className="vw-chip">👍 {tip.reactions?.like || 0}</span>
              <span className="vw-chip">🔥 {tip.reactions?.fire || 0}</span>
              <span className="vw-chip">💥 {tip.reactions?.wow || 0}</span>
            </div>

            <h1 className="vw-title">{tip.title}</h1>

            {tip.summary?.trim?.() && (
              <Section id="summary">
                <div className="vw-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{tip.summary}</ReactMarkdown>
                </div>
              </Section>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="vw-grid">
          <main className="vw-main">
            {tip.content?.trim?.() && (
              <Section id="content" title="Guide">
                <div className="vw-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{tip.content}</ReactMarkdown>
                </div>
              </Section>
            )}

            {Array.isArray(tip.gallery) && tip.gallery.length > 0 && (
              <Section id="gallery" title="Gallery">
                <div className="vw-gallery-grid">
                  {tip.gallery.map((src, i) => (
                    <button key={i} className="vw-shotbtn" onClick={() => openLightbox(i)} aria-label={`Open image ${i + 1}`}>
                      <img className="vw-shot" src={src} alt={`Image ${i + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {tip.videoUrl && (
              <Section id="video" title="Video">
                {yt ? (
                  <div className="vw-video">
                    <iframe
                      title="Tip video"
                      src={yt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <a className="vw-link" href={tip.videoUrl} target="_blank" rel="noreferrer">▶ Open video</a>
                )}
              </Section>
            )}

            <div className="vw-engage">
              <PresenceBadge presenceKey="tips_tricks" entityId={tip.id} />
              <LikeBar basePath={["tips_tricks", tip.id]} />
              <ReactionBar basePath={["tips_tricks", tip.id]} />
              <Bookmark bookmarkKey={`tips:${tip.id}`} />
            </div>

            <Discussion basePath={["tips_tricks", tip.id]} />
          </main>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(tip.gallery) && tip.gallery.length > 0 && (
        <div className="vw-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Image viewer">
          <button className="vw-lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
          <button className="vw-lightbox-nav left" onClick={prevLight} aria-label="Previous">‹</button>
          <img className="vw-lightbox-img" src={tip.gallery[lightbox.index]} alt={`Image ${lightbox.index + 1}`} />
          <button className="vw-lightbox-nav right" onClick={nextLight} aria-label="Next">›</button>
        </div>
      )}
    </PageWrapper>
  );
}
